import { Switch, Route, useLocation } from "wouter";
import { Toaster } from "@/components/ui/toaster";
import NotFound from "@/pages/not-found";
import Layout from "@/components/layout/layout";
import FieldMappings from "@/pages/field-mappings";
import ChoiceMappings from "@/pages/choice-mappings";
import AutoDiscovery from "@/pages/auto-discovery";
import EntitySyncLogs from "@/pages/entity-sync-logs";
import EntityMappings from "@/pages/entity-mappings";
import FieldSyncLogs from "@/pages/field-sync-logs";
import Tasks from "@/pages/tasks";
import TaskLogs from "@/pages/task-logs";
import WelcomePage from "@/pages/welcome";
import ApiStatusPage from "@/pages/api-status";
import RateMonitor from "@/pages/ratemonitor";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { useEffect, useState } from "react";
import { useAuthProvider } from "./hooks/use-azure-auth-provider";

function Router() {
  // Use the Azure auth provider
  const { isSignedIn, isLoaded } = useAuthProvider();
  const [location, setLocation] = useLocation();

  // Redirect based on authentication status
  useEffect(() => {
    if (isLoaded) {
      if (isSignedIn && (location === "/" || location === "/welcome")) {
        setTimeout(() => {
          console.log("Redirecting authenticated user to field-mappings");
          setLocation("/field-mappings");
        }, 100);
      } else if (!isSignedIn && location !== "/" && location !== "/welcome") {
        setLocation("/welcome");
      }
    }
  }, [isLoaded, isSignedIn, location, setLocation]);

  return (
    <Switch>
      <Route path="/" component={WelcomePage} />
      <Route path="/welcome" component={WelcomePage} />
      
      {/* Protected Routes */}
      <Route path="/field-mappings">
        <ProtectedRoute>
          <FieldMappings />
        </ProtectedRoute>
      </Route>
      <Route path="/choice-mappings">
        <ProtectedRoute>
          <ChoiceMappings />
        </ProtectedRoute>
      </Route>
      <Route path="/auto-discovery">
        <ProtectedRoute>
          <AutoDiscovery />
        </ProtectedRoute>
      </Route>
      <Route path="/entity-sync-logs">
        <ProtectedRoute>
          <EntitySyncLogs />
        </ProtectedRoute>
      </Route>
  
      
      <Route path="/entity-mappings">
        <ProtectedRoute>
          <EntityMappings />
        </ProtectedRoute>
      </Route>
      
      <Route path="/field-sync-logs">
        <ProtectedRoute>
          <FieldSyncLogs />
        </ProtectedRoute>
      </Route>
      
      <Route path="/tasks">
        <ProtectedRoute>
          <Tasks />
        </ProtectedRoute>
      </Route>
      
      <Route path="/task-logs">
        <ProtectedRoute>
          <TaskLogs />
        </ProtectedRoute>
      </Route>
      
      <Route path="/ratemonitor">
        <ProtectedRoute>
          <RateMonitor />
        </ProtectedRoute>
      </Route>
      
      <Route path="/api-status">
        <ProtectedRoute>
          <ApiStatusPage />
        </ProtectedRoute>
      </Route>
      
      <Route component={NotFound} />
    </Switch>
  );
}

function AppContent() {
  const { isSignedIn, isLoaded } = useAuthProvider();
  
  // Log auth status for debugging
  useEffect(() => {
    if (isLoaded) {
      console.log('App initialized with authentication. User signed in:', isSignedIn);
    }
  }, [isLoaded, isSignedIn]);
  
  // Show loading state while auth is still being determined
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-500">Loading authentication state...</p>
        </div>
      </div>
    );
  }
  

  // In production, only show layout for authenticated users
  return isSignedIn ? (
    <Layout>
      <Router />
    </Layout>
  ) : (
    <Router />
  );
}

function App() {
  const [mounted, setMounted] = useState(false);

  // Only mount once to avoid hydration issues
  useEffect(() => {
    setMounted(true);
    
    // Add Material Icons font loading detection
    document.body.classList.add('js-material-icons-pending');
    
    // Check if Material Icons font is available
    const checkFontLoaded = async () => {
      try {
        // Use Font Face Observer pattern
        const isFontLoaded = await new Promise((resolve) => {
          if (document.fonts) {
            // Modern browsers support
            document.fonts.ready.then(() => {
              // Create a test element with Material Icons
              const testElement = document.createElement('span');
              testElement.style.fontFamily = "'Material Icons'";
              testElement.style.fontSize = '24px';
              testElement.style.visibility = 'hidden';
              testElement.textContent = 'check';
              document.body.appendChild(testElement);
              
              // Check if the font loaded
              const fontLoaded = testElement.offsetWidth > 0;
              document.body.removeChild(testElement);
              resolve(fontLoaded);
            });
          } else {
            // Fallback for older browsers
            // Assume it's loaded after a timeout
            setTimeout(() => resolve(true), 500);
          }
        });
        
        // Update the body class based on font loading status
        document.body.classList.remove('js-material-icons-pending');
        if (isFontLoaded) {
          document.body.classList.add('js-material-icons-loaded');
        } else {
          document.body.classList.add('js-material-icons-failed');
          console.warn('Material Icons font failed to load');
        }
      } catch (error) {
        document.body.classList.remove('js-material-icons-pending');
        document.body.classList.add('js-material-icons-failed');
        console.error('Error checking Material Icons font:', error);
      }
    };
    
    checkFontLoaded();
  }, []);
  
  if (!mounted) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-500">Initializing application...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <AppContent />
      <Toaster />
    </>
  );
}

export default App;
